import { getConnectivityMatrix } from '../util/getConnectivityMatrix.js';
import { getCanonizedDiaIDs } from './getCanonizedDiaIDs';
import { getCanonizedHoseCodes } from './getCanonizedHoseCodes';
import { getDiaIDsAndInfo } from './getDiaIDsAndInfo';
import { getHeterotopicSymmetryRanks, getFinalRanks, } from './getHeterotopicSymmetryRanks';
import { getMoleculeWithH } from './getMoleculeWithH';
import { getXMolecule } from './getXMolecule';
/**
 * This class deals with topicity information and hose codes
 * It is optimized to avoid recalculation of the same information
 */
export class TopicMolecule {
    constructor(molecule) {
        this.originalMolecule = molecule;
        this.idCode = molecule.getIDCode();
        this.molecule = this.originalMolecule.getCompactCopy();
        this.molecule.ensureHelperArrays(molecule.getOCL().Molecule.cHelperNeighbours);
        this.Molecule = this.molecule.getOCL().Molecule;
        //@ts-expect-error TODO
        this.molecule.ensureHelperArrays(this.Molecule.cHelperNeighbours);
        this.cache = {};
    }
    toMolfile(options = {}) {
        const { version = 2 } = options;
        if (version === 2) {
            return this.molecule.toMolfile();
        }
        return this.molecule.toMolfileV3();
    }
    getMolecule() {
        return this.molecule;
    }
    /**
     * Returns a new TopicMolecule but will copy precalculated information
     * if possible (same idCode). This is very practical when expanding hydrogens
     * for example.
     * @param molecule
     * @returns
     */
    fromMolecule(molecule) {
        const idCode = molecule.getIDCode();
        if (idCode !== this.idCode) {
            // no way for optimisation
            return new TopicMolecule(molecule);
        }
        const topicMolecule = new TopicMolecule(molecule);
        topicMolecule.cache = {
            canonizedDiaIDs: this.cache.canonizedDiaIDs,
            canonizedHoseCodes: this.cache.canonizedHoseCodes,
        };
        return topicMolecule;
    }
    /**
     * Returns a molecule with all the hydrogens added. The order is NOT canonized
     */
    get moleculeWithH() {
        if (this.cache.moleculeWithH)
            return this.cache.moleculeWithH;
        this.cache.moleculeWithH = getMoleculeWithH(this.molecule);
        return this.cache.moleculeWithH;
    }
    get xMolecule() {
        if (this.cache.xMolecule)
            return this.cache.xMolecule;
        this.cache.xMolecule = getXMolecule(this.moleculeWithH);
        return this.cache.xMolecule;
    }
    /**
     * This is related to the current moleculeWithH. The order is NOT canonized
     */
    get diaIDs() {
        if (this.cache.diaIDs)
            return this.cache.diaIDs;
        const diaIDs = [];
        for (let i = 0; i < this.moleculeWithH.getAllAtoms(); i++) {
            diaIDs.push(this.canonizedDiaIDs[this.finalRanks[i]]);
        }
        this.cache.diaIDs = diaIDs;
        return diaIDs;
    }
    /**
     * This is related to the current moleculeWithH. The order is NOT canonized
     */
    get hoseCodes() {
        if (this.cache.hoseCodes)
            return this.cache.hoseCodes;
        const hoseCodes = [];
        for (let i = 0; i < this.moleculeWithH.getAllAtoms(); i++) {
            hoseCodes.push(this.canonizedHoseCodes[this.finalRanks[i]]);
        }
        this.cache.hoseCodes = hoseCodes;
        return hoseCodes;
    }
    get canonizedDiaIDs() {
        if (this.cache.canonizedDiaIDs)
            return this.cache.canonizedDiaIDs;
        this.cache.canonizedDiaIDs = getCanonizedDiaIDs(this);
        return this.cache.canonizedDiaIDs;
    }
    get canonizedHoseCodes() {
        if (this.cache.canonizedHoseCodes) {
            return this.cache.canonizedHoseCodes;
        }
        this.cache.canonizedHoseCodes = getCanonizedHoseCodes(this);
        return this.cache.canonizedHoseCodes;
    }
    /**
     * Returns the distance matrix for the current moleculeWithH
     */
    get distanceMatrix() {
        return getConnectivityMatrix(this.moleculeWithH, { pathLength: true });
    }
    get diaIDsAndInfo() {
        if (this.cache.diaIDsAndInfo)
            return this.cache.diaIDsAndInfo;
        this.cache.diaIDsAndInfo = getDiaIDsAndInfo(this, this.canonizedDiaIDs);
        return this.cache.diaIDsAndInfo;
    }
    /**
     * Returns symmetryRanks for all the atoms including hydrogens. Those ranks
     * deals with topicity and is related to the current moleculeWithH.
     * In order to calculate the ranks we replace all the
     * hydrogens with a X atom.
     */
    get heterotopicSymmetryRanks() {
        if (this.cache.heterotopicSymmetryRanks) {
            return this.cache.heterotopicSymmetryRanks;
        }
        this.cache.heterotopicSymmetryRanks = getHeterotopicSymmetryRanks(this.xMolecule);
        return [...this.cache.heterotopicSymmetryRanks];
    }
    /**
     * Returns finalRanks for all the atoms including hydrogens. Those ranks
     * deals with topicity and is related to the current moleculeWithH.
     * All the atoms have a unique identifier.j
     * In order to calculate the ranks we replace all the
     * hydrogens with a X atom.
     */
    get finalRanks() {
        if (this.cache.finalRanks)
            return this.cache.finalRanks;
        this.cache.finalRanks = getFinalRanks(this.xMolecule);
        return this.cache.finalRanks;
    }
    toMolfileWithH(options = {}) {
        const { version = 2 } = options;
        if (version === 2) {
            return this.moleculeWithH.toMolfile();
        }
        return this.moleculeWithH.toMolfileV3();
    }
    /**
     * Returns an array of objects containing the oclID and the corresponding hydrogens and atoms
     * for the specified atomLabel (if any)
     * This always applied to the molecule with expanded hydrogens and chirality
     * @param options
     * @returns
     */
    getGroupedDiastereotopicAtomIDs(options = {}) {
        return groupDiastereotopicAtomIDs(this.diaIDs, this.moleculeWithH, options);
    }
}
export function groupDiastereotopicAtomIDs(diaIDs, molecule, options = {}) {
    const { atomLabel } = options;
    const diaIDsObject = {};
    for (let i = 0; i < diaIDs.length; i++) {
        if (!atomLabel || molecule.getAtomLabel(i) === atomLabel) {
            const diaID = diaIDs[i];
            if (!diaIDsObject[diaID]) {
                diaIDsObject[diaID] = {
                    counter: 0,
                    atoms: [],
                    oclID: diaID,
                    atomLabel: molecule.getAtomLabel(i),
                };
            }
            diaIDsObject[diaID].counter++;
            diaIDsObject[diaID].atoms.push(i);
        }
    }
    return Object.keys(diaIDsObject).map((key) => diaIDsObject[key]);
}
//# sourceMappingURL=TopicMolecule.js.map